import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { useCart } from '../store/cartStore';
import { CheckoutProvider, useCheckout } from '../contexts/CheckoutContext';
import { getDefaultPaymentProcessor } from '../services/paymentGateway';
import { useCheckoutAbandonment } from '../hooks/useCheckoutAbandonment';
import { checkoutApi } from '../services/checkoutApi';

// Import checkout step components
import OrderReviewStep from '../components/checkout/OrderReviewStep';
import CustomerInfoStep from '../components/checkout/CustomerInfoStep';
import StripePaymentMethodStep from '../components/checkout/StripePaymentMethodStep';
import OrderConfirmationStep from '../components/checkout/OrderConfirmationStep';
import CheckoutProgress from '../components/checkout/CheckoutProgress';
import TimerCountdown from '../components/checkout/TimerCountdown';
import CheckoutSummary from '../components/checkout/CheckoutSummary';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const CheckoutHeader = styled.div`
  margin-bottom: 2rem;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }
  }
  
  h1 {
    color: #333;
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
  
  .subtitle {
    color: #666;
    font-size: 1.1rem;
  }
`;

const CancelButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
  }
`;

const CheckoutContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const MainContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const SidebarContent = styled.div`
  position: sticky;
  top: 2rem;
  
  @media (max-width: 768px) {
    position: static;
    order: -1;
  }
`;

const ErrorContainer = styled.div`
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #c53030;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .icon {
    font-size: 1.2rem;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  
  .spinner {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    
    .loading-icon {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
    
    h3 {
      margin: 0 0 0.5rem 0;
      color: #333;
    }
    
    p {
      margin: 0;
      color: #666;
      font-size: 0.9rem;
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Main checkout component wrapped with provider
const CheckoutPageContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const { cartItems, prepareForCheckout, clearCart } = useCart();
  const { state, actions } = useCheckout();

  const [paymentProcessor, setPaymentProcessor] = useState(null);

  // Handle checkout abandonment - automatically release tickets if user leaves
  const { abandonCheckout, isAbandoning } = useCheckoutAbandonment(state.orderId, {
    onAbandon: () => {
      console.log('🚨 Checkout was abandoned, tickets and cart have been cleared');
      // Clear checkout state
      actions.resetCheckout();
      // Clear cart state since backend cleared cart reservations
      clearCart();
    },
    enabled: !!state.orderId && state.currentStep > 1, // Only enable after order is created
    excludePaths: ['/checkout/success', '/checkout/complete', '/profile/orders']
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          from: location.pathname,
          message: 'Please log in to continue with checkout'
        }
      });
      return;
    }
  }, [isAuthenticated, navigate, location]);

  // Initialize payment processor
  useEffect(() => {
    const initializePayment = async () => {
      try {
        const processor = getDefaultPaymentProcessor();
        await processor.initialize();
        setPaymentProcessor(processor);
      } catch (error) {
        console.error('Failed to initialize payment processor:', error);
        actions.setGeneralError('Failed to initialize payment system. Please refresh and try again.');
      }
    };

    initializePayment();
  }, [actions]);

  const startCheckoutProcess = useCallback(async () => {
    try {
      actions.setValidating(true);

      // Prepare cart items for checkout
      const cartPayload = await prepareForCheckout();

      const payload = {
        userId: user.id,
        cartItems: cartPayload,
        currencyCode: 'USD',
        successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/checkout/cancel`
      };
      console.log('🚀 Starting checkout with payload:', payload);

      // Call backend to start checkout
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/checkout/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start checkout');
      }

      const result = await response.json();

      if (!result.success || !result.order) {
        throw new Error(result.message || 'Checkout failed');
      }

      // Set order information (Transient/Preview)
      actions.setOrder(result.order, result.expiresAt);

      // REDIRECT TO STRIPE IMMEDIATELY if URL is provided
      if (result.redirectUrl) {
        console.log('🔄 Redirecting to Stripe Checkout:', result.redirectUrl);
        actions.setLoading(true); // Keep loading state
        window.location.href = result.redirectUrl;
        return; // Stop further processing
      }

      // Auto-fill customer info if available
      if (user) {
        actions.updateCustomerInfo({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || ''
        });
      }

    } catch (error) {
      console.error('Start checkout error:', error);
      throw error;
    } finally {
      actions.setValidating(false);
    }
  }, [actions, prepareForCheckout, user]);

  // Initialize checkout from cart or existing order
  useEffect(() => {
    const initializeCheckout = async () => {
      if (!user || !isAuthenticated) return;

      try {
        actions.setLoading(true);
        actions.clearGeneralError();

        // Check if we have an existing order from navigation state
        const existingOrder = location.state?.orderId;
        const existingOrderNumber = location.state?.orderNumber;
        const existingExpiresAt = location.state?.expiresAt;

        if (existingOrder && existingOrderNumber) {
          // Resume existing checkout
          actions.setOrder({
            id: existingOrder,
            orderNumber: existingOrderNumber
          }, existingExpiresAt);
          actions.setStep(3); // Go to payment step
        } else {
          // Start new checkout from cart
          if (cartItems.length === 0) {
            navigate('/cart', {
              state: { message: 'Your cart is empty. Please add some tickets first.' }
            });
            return;
          }

          // Start checkout process
          await startCheckoutProcess();
        }
      } catch (error) {
        console.error('Checkout initialization error:', error);
        actions.setGeneralError(error.message || 'Failed to initialize checkout');
      } finally {
        actions.setLoading(false);
      }
    };

    initializeCheckout();
  }, [user, isAuthenticated, location.state, cartItems.length, actions, navigate, startCheckoutProcess]);

  // Timer for order expiration
  useEffect(() => {
    if (!state.expiresAt) return;

    const updateTimer = () => {
      const now = new Date();
      const expiresAt = new Date(state.expiresAt);
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));

      actions.setTimeRemaining(remaining);

      if (remaining === 0) {
        actions.setGeneralError('Your reservation has expired. Please start checkout again.');
        setTimeout(() => {
          navigate('/cart');
        }, 3000);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [state.expiresAt, actions, navigate]);

  const completeCheckout = async (paymentData) => {
    try {
      actions.setProcessingPayment(true);

      // Use the checkoutApi which properly handles authentication
      const result = await checkoutApi.completeCheckout(state.orderId, {
        method: paymentData.method || 'card',
        reference: paymentData.referenceCode || paymentData.paymentIntentId,
        amount: state.order?.totalAmount,
        currencyCode: 'USD'
      });

      if (!result.success) {
        throw new Error(result.error || 'Checkout completion failed');
      }

      // Clear cart and navigate to success
      await clearCart();

      navigate('/checkout/success', {
        state: {
          order: result.order,
          payment: result.payment
        }
      });

    } catch (error) {
      console.error('Complete checkout error:', error);
      actions.setGeneralError(error.message || 'Payment failed');
      throw error;
    } finally {
      actions.setProcessingPayment(false);
    }
  };

  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <OrderReviewStep
            order={state.order}
            onNext={actions.nextStep}
          />
        );
      case 2:
        return (
          <CustomerInfoStep
            customerInfo={state.customerInfo}
            billingInfo={state.billingInfo}
            errors={state.errors}
            onUpdateCustomer={actions.updateCustomerInfo}
            onUpdateBilling={actions.updateBillingInfo}
            onNext={actions.nextStep}
            onPrev={actions.prevStep}
            onClearError={actions.clearError}
          />
        );
      case 3:
        return (
          <StripePaymentMethodStep
            paymentProcessor={paymentProcessor}
            order={state.order}
            customerInfo={state.customerInfo}
            billingInfo={state.billingInfo}
            isProcessing={state.isProcessingPayment}
            errors={state.errors}
            onPaymentComplete={completeCheckout}
            onPrev={actions.prevStep}
            onClearError={actions.clearError}
          />
        );
      case 4:
        return (
          <OrderConfirmationStep
            order={state.order}
            customerInfo={state.customerInfo}
          />
        );
      default:
        return <OrderReviewStep order={state.order} onNext={actions.nextStep} />;
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <PageContainer>
      <Helmet>
        <title>Checkout - TikPlat</title>
        <meta name="description" content="Complete your ticket purchase securely" />
      </Helmet>

      <Container>
        <CheckoutHeader>
          <div className="header-content">
            <div>
              <h1>Secure Checkout</h1>
              <p className="subtitle">Complete your ticket purchase securely</p>
            </div>
            {state.orderId && (
              <CancelButton
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel checkout? Your tickets and cart will be cleared.')) {
                    abandonCheckout('USER_CANCELLED');
                    // Clear cart since backend will clear reservations
                    clearCart();
                    navigate('/cart', {
                      state: { checkoutAbandoned: true }
                    });
                  }
                }}
                disabled={isAbandoning}
              >
                {isAbandoning ? 'Cancelling...' : 'Cancel Checkout'}
              </CancelButton>
            )}
          </div>
        </CheckoutHeader>

        {state.generalError && (
          <ErrorContainer>
            <span className="icon">⚠️</span>
            <span>{state.generalError}</span>
          </ErrorContainer>
        )}

        <CheckoutContent>
          <MainContent>
            <CheckoutProgress
              currentStep={state.currentStep}
              totalSteps={state.totalSteps}
              validatedSteps={state.validatedSteps}
            />

            {renderCurrentStep()}
          </MainContent>

          <SidebarContent>
            {state.expiresAt && (
              <TimerCountdown
                expiresAt={state.expiresAt}
                timeRemaining={state.timeRemaining}
              />
            )}

            <CheckoutSummary
              order={state.order}
              cartItems={cartItems}
              showDetails={state.currentStep >= 2}
            />
          </SidebarContent>
        </CheckoutContent>
      </Container>

      {(state.isLoading || state.isValidating || state.isProcessingPayment) && (
        <LoadingOverlay>
          <div className="spinner">
            <div className="loading-icon"></div>
            <h3>
              {state.isProcessingPayment ? 'Processing Payment...' :
                state.isValidating ? 'Validating Cart...' :
                  'Loading...'}
            </h3>
            <p>
              {state.isProcessingPayment ? 'Please do not close this window' :
                state.isValidating ? 'Checking ticket availability' :
                  'Please wait'}
            </p>
          </div>
        </LoadingOverlay>
      )}
    </PageContainer>
  );
};

// Main component with context provider
const CheckoutPage = () => {
  return (
    <CheckoutProvider>
      <CheckoutPageContent />
    </CheckoutProvider>
  );
};

export default CheckoutPage;
